import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import milestonesApi from '../utils/milestonesApi';

const fmtDate = (d) => { if (!d) return '—'; const x = new Date(d); return `${String(x.getDate()).padStart(2, '0')}/${String(x.getMonth() + 1).padStart(2, '0')}/${x.getFullYear()}`; };
const inp = { width: '100%', padding: '9px 11px', border: '1px solid #D1D5DB', borderRadius: 8, fontSize: 13, boxSizing: 'border-box' };
const lbl = { fontSize: 12, fontWeight: 600, color: '#6B7280', marginBottom: 5, display: 'block' };
const btn = (bg = '#4F46E5', c = '#fff') => ({ padding: '9px 16px', background: bg, color: c, border: '1px solid ' + bg, borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer' });
const card = { background: '#fff', border: '1px solid #E5E7EB', borderRadius: 12, padding: 18, marginBottom: 18 };
const th = { padding: '9px 12px', fontSize: 11, fontWeight: 700, color: '#6B7280', textTransform: 'uppercase', letterSpacing: '.4px', textAlign: 'left', borderBottom: '1px solid #E5E7EB' };
const td = { padding: '9px 12px', fontSize: 13, borderBottom: '1px solid #F1F3F5' };

const STATUS_OPTIONS = [
  { value: 'not_started', label: 'Not Started', color: '#8A93A6', bg: '#F1F5F9' },
  { value: 'planning', label: 'Planning', color: '#2563EB', bg: '#DBEAFE' },
  { value: 'in_progress', label: 'In Progress', color: '#5B5BD6', bg: '#EEF2FF' },
  { value: 'at_risk', label: 'At Risk', color: '#D97706', bg: '#FEF3C7' },
  { value: 'on_hold', label: 'On Hold', color: '#8A93A6', bg: '#F1F5F9' },
  { value: 'escalated', label: 'Escalated', color: '#DC2626', bg: '#FEE2E2' },
  { value: 'completed', label: 'Completed', color: '#16A34A', bg: '#DCFCE7' },
  { value: 'cancelled', label: 'Cancelled', color: '#EF4444', bg: '#FEE2E2' },
];
const PRIORITY_OPTIONS = [
  { value: 'urgent', label: '🔴 Urgent', color: '#DC2626', bg: '#FEE2E2' },
  { value: 'high', label: '🟠 High', color: '#D97706', bg: '#FEF3C7' },
  { value: 'normal', label: '🔵 Normal', color: '#2563EB', bg: '#DBEAFE' },
  { value: 'low', label: '⚪ Low', color: '#8A93A6', bg: '#F1F5F9' },
];
const STATUS_MAP = Object.fromEntries(STATUS_OPTIONS.map(s => [s.value, s]));
const PRIORITY_MAP = Object.fromEntries(PRIORITY_OPTIONS.map(p => [p.value, p]));

function StatusPill({ value }) {
  const s = STATUS_MAP[value] || STATUS_OPTIONS[0];
  return <span style={{ fontSize: 11, fontWeight: 700, padding: '3px 10px', borderRadius: 99, background: s.bg, color: s.color, whiteSpace: 'nowrap' }}>{s.label}</span>;
}
function PriorityPill({ value }) {
  const p = PRIORITY_MAP[value] || PRIORITY_MAP.normal;
  return <span style={{ fontSize: 11, fontWeight: 700, padding: '3px 10px', borderRadius: 99, background: p.bg, color: p.color, whiteSpace: 'nowrap' }}>{p.label}</span>;
}
function MiniRing({ value = 0, size = 30 }) {
  const pct = Math.max(0, Math.min(100, Math.round(value || 0)));
  const r = (size - 4) / 2, circ = 2 * Math.PI * r, off = circ - (pct / 100) * circ;
  const color = pct >= 100 ? '#16A34A' : pct >= 75 ? '#16A34A' : pct >= 50 ? '#5B5BD6' : pct >= 25 ? '#D97706' : '#DC2626';
  return (
    <div style={{ position: 'relative', width: size, height: size, flexShrink: 0 }}>
      <svg width={size} height={size} style={{ transform: 'rotate(-90deg)' }}>
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="#E5E7EB" strokeWidth="3" />
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke={color} strokeWidth="3" strokeDasharray={circ} strokeDashoffset={off} strokeLinecap="round" />
      </svg>
      <span style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: size * 0.3, fontWeight: 700, color }}>{pct}</span>
    </div>
  );
}

function Modal({ title, onClose, children, width = 480 }) {
  return (
    <div
      style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 16 }}>
      <div style={{ background: '#fff', borderRadius: 12, width, maxWidth: '95vw', maxHeight: '90vh', overflowY: 'auto', boxShadow: '0 20px 60px rgba(0,0,0,.25)' }}>
        <div style={{ padding: '16px 20px', borderBottom: '1px solid #E5E7EB', display: 'flex', alignItems: 'center' }}>
          <h3 style={{ fontSize: 15, fontWeight: 800, flex: 1, margin: 0 }}>{title}</h3>
          <button onClick={onClose} style={{ border: 'none', background: 'none', fontSize: 20, cursor: 'pointer', color: '#6B7280' }}>×</button>
        </div>
        <div style={{ padding: 20 }}>{children}</div>
      </div>
    </div>
  );
}

export default function MajorMilestonesPage() {
  const { spaceId } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState({ tasks: [], standalone: [] });
  const [members, setMembers] = useState([]);
  const [modal, setModal] = useState(null);
  const [confirm, setConfirm] = useState(null);
  const [denied, setDenied] = useState(false);
  const [editRemark, setEditRemark] = useState(null); // { key, source, id, value }
  const [remarkModal, setRemarkModal] = useState(null); // list-task remark editor
  const now = new Date();
  const [fYear, setFYear] = useState('all'); // 'all' or a year number
  const [fMonth, setFMonth] = useState(now.getMonth() + 1);
  const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  const years = Array.from({ length: (now.getFullYear() + 1 - 2022) + 1 }, (_, i) => (now.getFullYear() + 1) - i);

  const load = useCallback(() => {
    milestonesApi.get(`/projects/${spaceId}/major-milestones`).then(setData).catch(e => { if (e.error === 'Access denied') setDenied(true); });
  }, [spaceId]);
  useEffect(() => { load(); milestonesApi.get(`/projects/${spaceId}/members`).then(setMembers).catch(() => {}); }, [load, spaceId]);
  useEffect(() => { const f = () => load(); window.addEventListener('focus', f); const t = setInterval(load, 15000); return () => { window.removeEventListener('focus', f); clearInterval(t); }; }, [load]);

  const saveRemark = async (m, value) => {
    try {
      if (m.source === 'task') await milestonesApi.put(`/projects/${spaceId}/task-remark/${m.id}`, { remark: value });
      else await milestonesApi.put(`/projects/${spaceId}/major-milestones/${m.id}`, { title: m.title, description: m.description, milestone_date: m.date, status: m.status, owner: m.owner, owner_id: m.owner_id, remark: value });
      setEditRemark(null); load();
    } catch (e) { alert(e.error || 'Failed to save remark'); }
  };

  if (denied) return (
    <div style={{ padding: 40, maxWidth: 600 }}>
      <button onClick={() => navigate(`/spaces/${spaceId}`)} style={{ ...btn('#F3F4F6', '#374151'), marginBottom: 20 }}>← Back to project</button>
      <div style={{ ...card, textAlign: 'center', padding: 40 }}><div style={{ fontSize: 38 }}>🔒</div><h2 style={{ fontSize: 17, fontWeight: 800 }}>Access restricted</h2></div>
    </div>
  );

  // Merge into one sorted list by date for a unified executive view.
  const merged = [...data.tasks, ...data.standalone].sort((a, b) => {
    if (!a.date && !b.date) return 0; if (!a.date) return 1; if (!b.date) return -1;
    return new Date(a.date) - new Date(b.date);
  });
  // Optional month/year filter (like Financial Analysis). 'all' shows everything;
  // a chosen year (optionally + month) filters by each item's date. Undated items
  // only show under "All years".
  // Month/year filter. Either can be used independently: pick a month to see that
  // month across years, a year to see that year, or both. Undated items are hidden
  // when any filter is active.
  const all = merged.filter(m => {
    const monthActive = fMonth && Number(fMonth) !== 0;
    const yearActive = fYear !== 'all';
    if (!monthActive && !yearActive) return true;
    if (!m.date) return false;
    const d = new Date(m.date);
    if (yearActive && d.getFullYear() !== Number(fYear)) return false;
    if (monthActive && (d.getMonth() + 1) !== Number(fMonth)) return false;
    return true;
  });

  return (
    <div style={{ padding: '24px 32px' }}>
      <div style={{ display: 'flex', alignItems: 'center', marginBottom: 6, gap: 12 }}>
        <button onClick={() => navigate(`/spaces/${spaceId}`)} style={btn('#F3F4F6', '#374151')}>← Project</button>
        <h1 style={{ fontSize: 22, fontWeight: 800, margin: 0, flex: 1 }}>⭐ Major Milestones &amp; Tasks</h1>
        <select value={fMonth} onChange={e => setFMonth(parseInt(e.target.value))} style={{ ...inp, width: 'auto' }} title="Filter to a specific month">
          <option value={0}>All months</option>
          {MONTHS.map((m, i) => <option key={i} value={i + 1}>{m}</option>)}
        </select>
        <select value={fYear} onChange={e => setFYear(e.target.value === 'all' ? 'all' : parseInt(e.target.value))} style={{ ...inp, width: 'auto' }} title="Filter by year">
          <option value="all">All years</option>
          {years.map(y => <option key={y} value={y}>{y}</option>)}
        </select>
        <button onClick={() => setModal({})} style={btn()}>+ Add Milestone</button>
      </div>
      <p style={{ fontSize: 13, color: '#6B7280', marginTop: 0, marginBottom: 18 }}>
        Every task flagged as a major milestone appears here automatically, alongside milestones you add directly. Used for monthly executive reporting.
      </p>


      <div style={card}>
        <h3 style={{ fontSize: 14, fontWeight: 800, marginTop: 0 }}>All Major Items ({all.length})</h3>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead><tr>
              <th style={th}>Date</th><th style={th}>Title</th><th style={th}>Source</th>
              <th style={th}>Status</th><th style={th}>Priority</th><th style={{ ...th, textAlign: 'center' }}>Progress</th>
              <th style={th}>Owner / Assignee</th><th style={th}>Remark</th><th style={th}></th>
            </tr></thead>
            <tbody>
              {all.length === 0 && <tr><td style={{ ...td, color: '#9CA3AF', textAlign: 'center' }} colSpan={9}>No major milestones yet. Flag a task as "Major task / milestone" or add one here.</td></tr>}
              {all.map(m => {
                const rkey = `${m.source}-${m.id}`;
                const editing = editRemark && editRemark.key === rkey;
                return (
                <tr key={rkey}>
                  <td style={{ ...td, whiteSpace: 'nowrap' }}>{fmtDate(m.date)}</td>
                  <td style={{ ...td, fontWeight: 600 }}>
                    {m.source === 'task'
                      ? <span onClick={() => setRemarkModal(m)} style={{ cursor: 'pointer' }} title="Click to add / edit this task's remark">⭐ {m.title}</span>
                      : <>⭐ {m.title}</>}
                  </td>
                  <td style={td}>
                    {m.source === 'task'
                      ? <span style={{ fontSize: 11, fontWeight: 700, padding: '2px 9px', borderRadius: 99, background: '#EEF2FF', color: '#4F46E5' }}>Task</span>
                      : <span style={{ fontSize: 11, fontWeight: 700, padding: '2px 9px', borderRadius: 99, background: '#F5F3FF', color: '#7C3AED' }}>Milestone</span>}
                  </td>
                  <td style={td}><StatusPill value={m.status} /></td>
                  <td style={td}><PriorityPill value={m.priority} /></td>
                  <td style={{ ...td, textAlign: 'center' }}><div style={{ display: 'flex', justifyContent: 'center' }}><MiniRing value={m.progress} /></div></td>
                  <td style={{ ...td, color: '#6B7280' }}>{(m.source === 'task' ? m.assignee : m.owner) || '—'}</td>
                  <td style={{ ...td, minWidth: 220, verticalAlign: 'top' }}>
                    {editing ? (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                        <textarea autoFocus value={editRemark.value} onChange={e => setEditRemark({ ...editRemark, value: e.target.value })}
                          onKeyDown={e => { if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) saveRemark(m, editRemark.value); if (e.key === 'Escape') setEditRemark(null); }}
                          style={{ ...inp, padding: '6px 8px', fontSize: 12.5, minHeight: 70, resize: 'vertical', lineHeight: 1.5 }}
                          placeholder={'Write a remark…\nUse new lines, "- " bullets or "1." numbering.'} />
                        <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                          <button onClick={() => saveRemark(m, editRemark.value)} style={{ ...btn('#4F46E5'), padding: '5px 12px' }}>Save</button>
                          <button onClick={() => setEditRemark(null)} style={{ ...btn('#F3F4F6', '#374151'), padding: '5px 10px' }}>Cancel</button>
                          <span style={{ fontSize: 10.5, color: '#9CA3AF' }}>⌘/Ctrl+Enter to save</span>
                        </div>
                      </div>
                    ) : (
                      <div onClick={() => setEditRemark({ key: rkey, value: m.remark || '' })} title="Click to edit"
                        style={{ cursor: 'pointer', fontSize: 12.5, color: m.remark ? '#374151' : '#9CA3AF', padding: '4px 5px', borderRadius: 5, whiteSpace: 'pre-wrap', lineHeight: 1.5 }}>
                        {m.remark ? m.remark : 'N/A — click to add'}
                      </div>
                    )}
                  </td>
                  <td style={{ ...td, textAlign: 'right', whiteSpace: 'nowrap' }}>
                    {m.source === 'milestone' ? (
                      <>
                        <button onClick={() => setModal(m)} style={{ ...btn('#F3F4F6', '#374151'), padding: '5px 10px', marginRight: 6 }}>Edit</button>
                        <button onClick={() => setConfirm(m)} style={{ ...btn('#FEF2F2', '#DC2626'), padding: '5px 10px', borderColor: '#FECACA' }}>Delete</button>
                      </>
                    ) : (
                      <button onClick={() => setRemarkModal(m)} style={{ ...btn('#F3F4F6', '#374151'), padding: '5px 10px' }} title="This task comes from a list. Edit its remark for the report here.">✏ Remark</button>
                    )}
                  </td>
                </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {modal && modal.source === 'milestone' &&
        <MilestoneModal spaceId={spaceId} row={modal} members={members} onClose={() => setModal(null)} onSaved={() => { setModal(null); load(); }} />}
      {remarkModal &&
        <RemarkModal spaceId={spaceId} row={remarkModal} onClose={() => setRemarkModal(null)} onSaved={() => { setRemarkModal(null); load(); }} />}
      {confirm && (
        <Modal title="Delete milestone" onClose={() => setConfirm(null)} width={420}>
          <p style={{ fontSize: 13, color: '#4B5563', marginTop: 0 }}>Delete "{confirm.title}"? This cannot be undone.</p>
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            <button onClick={() => setConfirm(null)} style={btn('#F3F4F6', '#374151')}>Cancel</button>
            <button onClick={async () => { await milestonesApi.delete(`/projects/${spaceId}/major-milestones/${confirm.id}`); setConfirm(null); load(); }} style={btn('#DC2626')}>Delete</button>
          </div>
        </Modal>
      )}
    </div>
  );
}

// Remark editor for a flagged TASK pulled from a list. Only the remark is editable
// here; the task itself is managed on its task board.
function RemarkModal({ spaceId, row, onClose, onSaved }) {
  const [remark, setRemark] = useState(row.remark || '');
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try {
      await milestonesApi.put(`/projects/${spaceId}/task-remark/${row.id}`, { remark });
      onSaved();
    } catch (e) { alert(e.error || 'Failed to save remark'); setBusy(false); }
  };
  return (
    <Modal title="Task Remark" onClose={onClose}>
      <div style={{ fontSize: 12.5, fontWeight: 700, color: '#111827', marginBottom: 4 }}>⭐ {row.title}</div>
      <div style={{ fontSize: 11.5, color: '#6B7280', marginBottom: 14 }}>
        This task comes from a list — only its remark is edited here. It appears in the monthly report.
      </div>
      <label style={lbl}>Remark</label>
      <textarea autoFocus style={{ ...inp, minHeight: 120, resize: 'vertical', lineHeight: 1.5 }}
        value={remark} onChange={e => setRemark(e.target.value)}
        onKeyDown={e => { if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) save(); }}
        placeholder={'Write a remark for the report…\nUse new lines, "- " bullets or "1." numbering.\nLeave empty to show "N/A".'} />
      <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', alignItems: 'center', marginTop: 16 }}>
        <span style={{ fontSize: 10.5, color: '#9CA3AF', marginRight: 'auto' }}>⌘/Ctrl+Enter to save</span>
        <button onClick={onClose} style={btn('#F3F4F6', '#374151')}>Cancel</button>
        <button onClick={save} disabled={busy} style={btn()}>{busy ? 'Saving…' : 'Save'}</button>
      </div>
    </Modal>
  );
}

function MilestoneModal({ spaceId, row, members = [], onClose, onSaved }) {
  const [title, setTitle] = useState(row.title || '');
  const [description, setDescription] = useState(row.description || '');
  const [date, setDate] = useState(row.date ? String(row.date).slice(0, 10) : '');
  const [status, setStatus] = useState(row.status || 'not_started');
  const [priority, setPriority] = useState(row.priority || 'normal');
  const [progress, setProgress] = useState(row.progress != null ? row.progress : 0);
  const initialOther = !row.owner_id && row.owner ? true : false;
  const [ownerId, setOwnerId] = useState(initialOther ? '__other__' : (row.owner_id || ''));
  const [ownerOther, setOwnerOther] = useState(initialOther ? row.owner : '');
  const [remark, setRemark] = useState(row.remark || '');
  const [busy, setBusy] = useState(false);
  const save = async () => {
    if (!title.trim()) return; setBusy(true);
    let owner = '', owner_id = null;
    if (ownerId === '__other__') { owner = ownerOther; owner_id = null; }
    else if (ownerId) { owner = (members.find(m => m.id === ownerId) || {}).full_name || ''; owner_id = ownerId; }
    const payload = { title, description, milestone_date: date || null, status, priority, progress: Math.max(0, Math.min(100, parseInt(progress) || 0)), owner, owner_id, remark };
    try {
      if (row.id) await milestonesApi.put(`/projects/${spaceId}/major-milestones/${row.id}`, payload);
      else await milestonesApi.post(`/projects/${spaceId}/major-milestones`, payload);
      onSaved();
    } catch (e) { alert(e.error || 'Failed'); setBusy(false); }
  };
  return (
    <Modal title={row.id ? 'Edit Milestone' : 'New Major Milestone'} onClose={onClose}>
      <label style={lbl}>Title</label>
      <input style={inp} value={title} onChange={e => setTitle(e.target.value)} placeholder="e.g. Site handover" />
      <div style={{ display: 'flex', gap: 10, marginTop: 12 }}>
        <div style={{ flex: 1 }}><label style={lbl}>Date</label><input style={inp} type="date" value={date} onChange={e => setDate(e.target.value)} /></div>
        <div style={{ flex: 1 }}><label style={lbl}>Status</label>
          <select style={inp} value={status} onChange={e => setStatus(e.target.value)}>
            {STATUS_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
        </div>
      </div>
      <div style={{ display: 'flex', gap: 10, marginTop: 12 }}>
        <div style={{ flex: 1 }}><label style={lbl}>Priority</label>
          <select style={inp} value={priority} onChange={e => setPriority(e.target.value)}>
            {PRIORITY_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
        </div>
        <div style={{ flex: 1 }}><label style={lbl}>Progress: {progress}%</label>
          <input type="range" min="0" max="100" step="5" value={progress} onChange={e => setProgress(e.target.value)} style={{ width: '100%', marginTop: 8, accentColor: '#4F46E5' }} />
        </div>
      </div>
      <label style={{ ...lbl, marginTop: 12 }}>Owner / Assignee</label>
      <select style={inp} value={ownerId} onChange={e => setOwnerId(e.target.value)}>
        <option value="">— Unassigned —</option>
        {members.map(m => <option key={m.id} value={m.id}>{m.full_name}</option>)}
        <option value="__other__">Other…</option>
      </select>
      {ownerId === '__other__' && <input style={{ ...inp, marginTop: 6 }} value={ownerOther} onChange={e => setOwnerOther(e.target.value)} placeholder="Type owner name" />}
      <label style={{ ...lbl, marginTop: 12 }}>Remark</label>
      <textarea style={{ ...inp, minHeight: 70, resize: 'vertical', lineHeight: 1.5 }} value={remark} onChange={e => setRemark(e.target.value)} placeholder={'Optional — shown in the report (N/A if empty).\nUse new lines, "- " bullets or "1." numbering.'} />
      <label style={{ ...lbl, marginTop: 12 }}>Description (optional)</label>
      <textarea style={{ ...inp, minHeight: 70 }} value={description} onChange={e => setDescription(e.target.value)} />
      <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 16 }}>
        <button onClick={onClose} style={btn('#F3F4F6', '#374151')}>Cancel</button>
        <button onClick={save} disabled={busy} style={btn()}>{busy ? 'Saving…' : 'Save'}</button>
      </div>
    </Modal>
  );
}
